import { useMemo, useState } from 'react';
import {
    Loader2,
    Wrench,
    Check,
    Type,
    Users,
    ChevronDown,
    ChevronRight,
    FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import type { UtilityTemplateGroup } from '@/services/api/broadcast';
import type { Page, UtilityConflictGroup } from '@/types/broadcast';
import type { MultiTemplateSelectionEntry } from '../types';
import UtilityOverlapResolver from './UtilityOverlapResolver';
import TemplateColumnsCanvas from './TemplateColumnsCanvas';

type OverlapResult = {
    conflictGroups: UtilityConflictGroup[];
    exclusiveByGroup: Record<string, string[]>;
    coveredPageIds: string[];
    uncoveredPageIds: string[];
} | null;

interface MultiTemplatePickerProps {
    pages: Page[];
    utilityGroups: UtilityTemplateGroup[];
    loadingGroups: boolean;
    selectedPages: Page[];
    onSelectedPagesChange: (pages: Page[]) => void;
    selection: MultiTemplateSelectionEntry[];
    onSelectionChange: (selection: MultiTemplateSelectionEntry[]) => void;
    conflictResolutions: Record<string, number>;
    onConflictResolutionsChange: (resolutions: Record<string, number>) => void;
    onReadyChange?: (ready: boolean) => void;
    onOverlapResultChange?: (result: OverlapResult) => void;
}

/** Numeral com peso visual — usado nos indicadores de etapa (1, 2, 3). */
function StepBadge({ n, active }: { n: number; active: boolean }) {
    return (
        <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors ${
                active ? 'bg-[#0f172a] text-white' : 'bg-gray-100 text-gray-400'
            }`}
        >
            {n}
        </span>
    );
}

/** Seção colapsável com cabeçalho consistente (número da etapa + título + resumo). */
function StepSection({
    step,
    title,
    summary,
    complete,
    defaultOpen,
    children,
}: {
    step: number;
    title: string;
    summary: string;
    complete: boolean;
    defaultOpen: boolean;
    children: React.ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50/60 transition-colors"
            >
                <StepBadge n={step} active={complete} />
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-500 truncate">{summary}</p>
                </div>
                {open ? (
                    <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                )}
            </button>
            {open && <div className="border-t border-gray-100 p-4">{children}</div>}
        </div>
    );
}

export default function MultiTemplatePicker({
    pages,
    utilityGroups,
    loadingGroups,
    selectedPages,
    onSelectedPagesChange,
    selection,
    onSelectionChange,
    conflictResolutions,
    onConflictResolutionsChange,
    onReadyChange,
    onOverlapResultChange,
}: MultiTemplatePickerProps) {
    const [activeCategoryIds, setActiveCategoryIds] = useState<string[]>([]);

    const pageOptions = useMemo(
        () => pages.map(p => ({ value: p.page_id, label: p.page_name, sublabel: p.page_id })),
        [pages]
    );

    const categories = useMemo(
        () =>
            Array.from(
                new Map(
                    pages
                        .filter(p => p.internal_category_id && p.internal_category_name)
                        .map(p => [p.internal_category_id, p.internal_category_name])
                ).entries()
            ).map(([id, name]) => ({ value: String(id), label: name as string })),
        [pages]
    );

    const filteredPages = useMemo(() => {
        if (activeCategoryIds.length === 0) return pages;
        return pages.filter(
            p => p.internal_category_id != null && activeCategoryIds.includes(String(p.internal_category_id))
        );
    }, [pages, activeCategoryIds]);

    const selectedPageIds = selectedPages.map(p => p.page_id);
    const someFilteredSelected = filteredPages.some(p => selectedPageIds.includes(p.page_id));

    const handlePagesChange = (ids: string[]) => {
        const byId = new Map(pages.map(p => [p.page_id, p]));
        const next = ids.map(id => byId.get(id)).filter((p): p is Page => !!p);
        onSelectedPagesChange(next);
    };

    const handleCategoryChange = (vals: string[]) => {
        const added = vals.filter(id => !activeCategoryIds.includes(id));
        const removed = activeCategoryIds.filter(id => !vals.includes(id));
        let next = [...selectedPages];
        if (removed.length > 0) {
            const toRemove = new Set(
                pages
                    .filter(p => p.internal_category_id != null && removed.includes(String(p.internal_category_id)))
                    .map(p => p.page_id)
            );
            next = next.filter(p => !toRemove.has(p.page_id));
        }
        if (added.length > 0) {
            const current = new Set(next.map(p => p.page_id));
            const toAdd = pages.filter(
                p =>
                    p.internal_category_id != null &&
                    added.includes(String(p.internal_category_id)) &&
                    !current.has(p.page_id)
            );
            next = [...next, ...toAdd];
        }
        setActiveCategoryIds(vals);
        onSelectedPagesChange(next);
    };

    const handleToggleAllFiltered = () => {
        if (someFilteredSelected) {
            const filteredIds = new Set(filteredPages.map(p => p.page_id));
            onSelectedPagesChange(selectedPages.filter(p => !filteredIds.has(p.page_id)));
        } else {
            const current = new Set(selectedPages.map(p => p.page_id));
            const toAdd = filteredPages.filter(p => !current.has(p.page_id));
            onSelectedPagesChange([...selectedPages, ...toAdd]);
        }
    };

    const templatesById = useMemo(() => new Map(utilityGroups.map(g => [g.id, g])), [utilityGroups]);

    const selectedGroupIds = new Set(selection.map(s => s.groupId));

    const sortedSelection = useMemo(
        () => [...selection].sort((a, b) => a.priority - b.priority),
        [selection]
    );

    const toggleTemplate = (group: UtilityTemplateGroup) => {
        if (selectedGroupIds.has(group.id)) {
            const next = selection
                .filter(s => s.groupId !== group.id)
                .map((s, idx) => ({ ...s, priority: idx }));
            onSelectionChange(next);
            return;
        }
        const entry: MultiTemplateSelectionEntry = {
            groupId: group.id,
            priority: selection.length,
            templateName: group.template_name,
            title: group.title,
            imageUrl: group.image_url ?? null,
            thumbnailUrl: group.thumbnail_url ?? null,
            approvedCount: group.approved_count,
        };
        onSelectionChange([...selection, entry]);
    };

    const removeTemplate = (groupId: number) => {
        onSelectionChange(
            selection.filter(s => s.groupId !== groupId).map((s, idx) => ({ ...s, priority: idx }))
        );
    };

    const handleSecondaryChange = (groupId: number, secondaryGroupId: number | null) => {
        onSelectionChange(
            selection.map(s => (s.groupId === groupId ? { ...s, secondaryGroupId } : s))
        );
    };

    const pagesComplete = selectedPages.length > 0;
    const templatesComplete = selection.length > 0;

    return (
        <div className="space-y-3">
            {/* Etapa 1 — Páginas alvo. Colapsa para um resumo compacto assim que
                houver seleção, em vez de expor centenas de chips soltos o tempo
                todo (showTags=false no select de páginas: a contagem já resume
                o essencial, edição fica atrás do "editar seleção"). */}
            <StepSection
                step={1}
                title="Páginas alvo"
                summary={
                    pagesComplete
                        ? `${selectedPages.length} página${selectedPages.length > 1 ? 's' : ''} selecionada${selectedPages.length > 1 ? 's' : ''}${activeCategoryIds.length > 0 ? ` · ${activeCategoryIds.length} categoria(s) filtrada(s)` : ''}`
                        : 'Nenhuma página selecionada ainda'
                }
                complete={pagesComplete}
                defaultOpen={!pagesComplete}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">
                            {filteredPages.length} página{filteredPages.length !== 1 ? 's' : ''} disponível{filteredPages.length !== 1 ? 'eis' : ''}
                        </span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleToggleAllFiltered}
                            className="h-7 text-xs text-[#0f172a] hover:bg-[#7dd3fc]/10"
                        >
                            {someFilteredSelected ? 'Desmarcar todas' : 'Selecionar todas'}
                            {activeCategoryIds.length > 0 ? ' (categoria)' : ''}
                        </Button>
                    </div>
                    {categories.length > 0 && (
                        <SearchableMultiSelect
                            options={categories}
                            values={activeCategoryIds}
                            onValuesChange={handleCategoryChange}
                            placeholder="Filtrar por categoria..."
                            searchPlaceholder="Buscar categoria..."
                            selectedLabel={(n) => `${n} categoria${n > 1 ? 's' : ''}`}
                        />
                    )}
                    <SearchableMultiSelect
                        options={pageOptions}
                        values={selectedPageIds}
                        onValuesChange={handlePagesChange}
                        placeholder="Selecione as páginas..."
                        searchPlaceholder="Buscar página..."
                        showTags={false}
                        selectedLabel={(n) => `${n} página${n > 1 ? 's' : ''} selecionada${n > 1 ? 's' : ''}`}
                    />
                </div>
            </StepSection>

            {/* Etapa 2 — Templates disponíveis, em grid de cards com preview
                (thumbnail real), não uma lista de texto. */}
            <StepSection
                step={2}
                title="Templates de utility"
                summary={
                    templatesComplete
                        ? `${selection.length} template${selection.length > 1 ? 's' : ''} selecionado${selection.length > 1 ? 's' : ''}, ordem define prioridade de conflito`
                        : 'Clique nos templates que quer usar nesta esteira'
                }
                complete={templatesComplete}
                defaultOpen={!templatesComplete}
            >
                {loadingGroups ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-[#7dd3fc] mr-2" />
                        <span className="text-gray-500 text-sm">Carregando templates...</span>
                    </div>
                ) : utilityGroups.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                        <Wrench className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Nenhum template utility encontrado</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 max-h-96 overflow-y-auto pr-1">
                        {utilityGroups.map(group => {
                            const isSelected = selectedGroupIds.has(group.id);
                            const entry = selection.find(s => s.groupId === group.id);
                            const priorityIndex = entry ? sortedSelection.findIndex(s => s.groupId === group.id) : -1;
                            return (
                                <button
                                    key={group.id}
                                    type="button"
                                    onClick={() => toggleTemplate(group)}
                                    className={`group relative flex flex-col overflow-hidden rounded-xl border-2 text-left transition-all ${
                                        isSelected
                                            ? 'border-[#0f172a] bg-[#0f172a]/[0.02] shadow-sm'
                                            : 'border-gray-200 bg-white hover:border-[#7dd3fc] hover:shadow-sm'
                                    }`}
                                >
                                    <div className="relative aspect-[4/3] w-full bg-gray-100 overflow-hidden">
                                        {group.thumbnail_url || group.image_url ? (
                                            <img
                                                src={group.thumbnail_url || group.image_url!}
                                                alt={group.title}
                                                className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center bg-blue-50">
                                                <Type className="h-6 w-6 text-blue-300" />
                                            </div>
                                        )}
                                        {isSelected && (
                                            <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#0f172a] text-[10px] font-bold text-white shadow">
                                                {priorityIndex + 1}
                                            </span>
                                        )}
                                        {isSelected && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-[#0f172a]/10">
                                                <Check className="h-6 w-6 text-white drop-shadow" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-2 space-y-0.5">
                                        <div className="flex items-center gap-1">
                                            <span className="shrink-0 rounded bg-gray-100 px-1 py-0.5 text-[9px] font-bold text-gray-500">
                                                #{group.id}
                                            </span>
                                            <h4 className="truncate text-xs font-medium text-gray-900">{group.template_name}</h4>
                                        </div>
                                        <div className="flex items-center gap-1 text-[10px] text-emerald-600">
                                            <Users className="h-2.5 w-2.5" />
                                            {group.approved_count ?? '?'} aprovada(s)
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </StepSection>

            {/* Etapa 3 — Canvas de colunas (peça central, sempre visível quando
                há templates selecionados, não colapsada). */}
            {sortedSelection.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50/60 to-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-900">Montagem visual</h3>
                        <span className="text-xs text-gray-400">
                            arraste para reordenar a prioridade
                        </span>
                    </div>
                    <TemplateColumnsCanvas
                        selection={sortedSelection}
                        templatesById={templatesById}
                        onReorder={onSelectionChange}
                        onRemove={removeTemplate}
                        onSecondaryChange={handleSecondaryChange}
                    />
                </div>
            )}

            {/* Cobertura e resolução de conflito — só dispara com 2+ templates e 1+ página */}
            <UtilityOverlapResolver
                selection={sortedSelection}
                pageIds={selectedPageIds}
                templatesById={templatesById}
                conflictResolutions={conflictResolutions}
                onConflictResolutionsChange={onConflictResolutionsChange}
                onReadyChange={(ready) => onReadyChange?.(ready)}
                onResultChange={onOverlapResultChange}
            />
        </div>
    );
}
